// -----------------------------------------------------------------------------
// SHELL — sidebar + page area.
// Two sidebar modes (decided 26/Sep/26):
//   * global  (outside a project): Projects · Admin console (super admin) · profile
//   * project (inside a project):  ‹ All projects · project switcher (search) ·
//                                  Dashboard · <modules the user can view> · Activity
// Every item shows its icon beside the name; collapsed (‹ button, remembered on this device) keeps
// only the icons with the name on hover, the K profile stays clickable, "v0.2" underneath
// (comments #1, #5 — 03/Oct/26; round 13 #3). Project setup sits below Activity (round 13 #7).
// Menus close on outside click or Esc (code list #1); ↑ / ↓ + Enter move through them (#8).
// Round 15 #1 (10/Oct/26): the sidebar never scrolls with the page — only the page area does; the
// sidebar never moves: its top and bottom are fixed and only the menu between them scrolls (no scroll bar) — v0.4.3.
// Esc / Backspace with nothing open: back to the previous page (code list #3) — on an issue page
// always back to its component list (round 13 #5).
// -----------------------------------------------------------------------------
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { goBack, href, leaveAdmin, leaveIssue, navigate, parseHash, type Route } from "../router";
import { useSession } from "../session";
import { useRpc } from "../api/useRpc";
import { sidebarModules } from "../access";
import { installEscKey, useEsc } from "../esc";
import { moduleEntryRoute } from "../../modules/registry";
import { CodeChip, focusMenu, InfoIcon, menuKeys, SearchBox, SideMenu, useFloatingTip, useOutsideAll } from "../ui/kit";
import { Icon, ICONS } from "../ui/icons";
import { APP_VERSION, POWERED_BY, RELEASE_DATE } from "../version";
import { formatDate } from "../format";
import { useBadge } from "../notifications";
import { BrandMark, BrandWordmark } from "../ui/brand";

const COLLAPSE_KEY = "buildnexx-sidebar-collapsed";
const readCollapsed = () => { try { return localStorage.getItem(COLLAPSE_KEY) === "1"; } catch { return false; } };
const MODULE_ICON: Record<string, string> = { issues: "issues", progress: "progress" };

/** "Powered by BIM Office Sdn. Bhd. · v0.2.1 · 03/Oct/26" (sidebar footer + login screen). */
export const VersionLine = () => <p className="powered" data-testid="version">{POWERED_BY} · v{APP_VERSION} · {formatDate(RELEASE_DATE)}</p>;

export function Shell({ route, projectCode, children }: { route: Route; projectCode: string | null; children: ReactNode }) {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  useEffect(() => installEscKey(), []);
  useEffect(() => { try { localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0"); } catch { /* private window */ } }, [collapsed]);
  useEsc("back", () => {
    const now = parseHash(window.location.hash);       // the page in the address bar right now (React may not have re-rendered yet)
    if (now.name === "issue") { leaveIssue(now); return true; }                        // round 25 #18
    if (now.name === "admin") { leaveAdmin(); return true; }                         // round 16 #10
    return goBack(now);
  });
  return (
    <div className={`shell${collapsed ? " collapsed" : ""}`}>
      <aside className="sidebar" aria-label="Sidebar">
        <button className="collapse-btn" aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"} onClick={() => setCollapsed(!collapsed)}><Icon name={collapsed ? "chevron_right" : "chevron_left"} /></button>
        {collapsed ? <>
          {projectCode
            ? <ProjectNavMini code={projectCode} route={route} admin={<AdminLink route={route} mini />} />
            : <SideParts top={<a className="brand mini" href={href({ name: "projects" })} aria-label="BuildNexx"><BrandMark size={26} label="BuildNexx" /></a>}
                menu={<NavIcon to={{ name: "projects" }} icon="home" name="Projects" active={route.name === "projects"} />} admin={<AdminLink route={route} mini />} />}
          <div className="side-bottom"><ProfileMenu mini /><p className="powered mini">v{APP_VERSION.split(".").slice(0, 2).join(".")}</p></div>
        </> : <>
          {projectCode
            ? <ProjectNav code={projectCode} route={route} admin={<AdminLink route={route} />} />
            : <SideParts top={<Brand />} menu={<GlobalNav route={route} />} admin={<AdminLink route={route} />} />}
          <div className="side-bottom"><ProfileMenu /><VersionLine /></div>
        </>}
      </aside>
      <main className="main"><div className="main-inner">{children}</div></main>
    </div>
  );
}

const Brand = () => <a className="brand" href={href({ name: "projects" })} aria-label="BuildNexx"><BrandMark size={26} /><BrandWordmark /></a>;

/**
 * Sidebar parts (v0.4.3, Kyle 11/Oct/26): the top (logo, All projects, switcher) and the bottom (profile, version) never
 * move; only the menu between them scrolls — with the mouse wheel, no scroll bar — when the window is too short.
 * A faint fade at the menu's bottom edge shows there is more below.
 */
function SideParts({ top, menu, admin }: { top: ReactNode; menu: ReactNode; admin?: ReactNode }) {
  const box = useRef<HTMLDivElement>(null);
  const [more, setMore] = useState(false);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const check = () => setMore(el.scrollTop + el.clientHeight < el.scrollHeight - 2);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    el.addEventListener("scroll", check);
    return () => { ro.disconnect(); el.removeEventListener("scroll", check); };
  }, []);
  return <>
    <div className="side-top">{top}</div>
    <div ref={box} className={`side-menu${more ? " more" : ""}`} data-testid="side-menu">{menu}<div className="grow" />{admin}</div>
  </>;
}

/** Sidebar item: icon + name. "Soon" items are greyed; the word "soon" shows on hover (no room for a tag beside the icon). */
const NavItem = ({ to, active, children, soon, icon }: { to?: Route; active?: boolean; children: string; soon?: boolean; icon: string }) => {
  const inner = <><span className="nav-ic">{ICONS[icon]}</span><span className="nav-name">{children}</span></>;
  return soon || !to
    ? <span className="nav-item disabled" title={soon ? `${children} · soon` : undefined} aria-label={soon ? `${children} (soon)` : children}>{inner}</span>
    : <a className={`nav-item${active ? " active" : ""}`} href={href(to)}>{inner}</a>;
};

/** Collapsed sidebar item: icon only; the name appears on hover (floating, never cut off). */
function NavIcon({ to, icon, name, active, soon }: { to?: Route; icon: string; name: string; active?: boolean; soon?: boolean }) {
  const { anchor, handlers, portal } = useFloatingTip(soon ? `${name} · soon` : name, { side: "right", className: "name" });
  const cls = `nav-icon${active ? " active" : ""}${soon ? " disabled" : ""}`;
  return <>
    {soon || !to
      ? <span ref={(el) => { anchor.current = el; }} className={cls} aria-label={name} tabIndex={0} {...handlers}>{ICONS[icon]}</span>
      : <a ref={(el) => { anchor.current = el; }} className={cls} href={href(to)} aria-label={name} {...handlers}>{ICONS[icon]}</a>}
    {portal}
  </>;
}

function GlobalNav({ route }: { route: Route }) {
  return <nav><NavItem icon="home" to={{ name: "projects" }} active={route.name === "projects"}>Projects</NavItem></nav>;
}

/** Project setup (A6, Part 4) shows for roles with the Project setup switch, Administrators and the super admin. */

function ProjectNav({ code, route, admin }: { code: string; route: Route; admin: ReactNode }) {
  const ctx = useRpc("project_context", { code });
  const showSetup = !!ctx.data?.can_setup;
  const mods = ctx.data ? sidebarModules(ctx.data.modules) : [];
  return (
    <SideParts admin={admin} top={<>
      <Brand />
      <a className="back" href={href({ name: "projects" })}><span className="nav-ic small">{ICONS.home}</span>All projects</a>
      <ProjectSwitcher code={code} name={ctx.data?.project.name ?? ""} />
    </>} menu={<nav>
      <NavItem icon="dashboard" to={{ name: "dashboard", code }} active={route.name === "dashboard"}>Dashboard</NavItem>
      {mods.map((m) => (
        <NavItem key={m.id} icon={MODULE_ICON[m.id] ?? "issues"} soon={m.state === "soon"} to={moduleEntryRoute(m.id, code)}
          active={m.id === "issues" && ["issues", "component", "issue"].includes(route.name)}>{m.name}</NavItem>
      ))}
      <NavItem icon="activity" to={{ name: "activity", code }} active={route.name === "activity"}>Activity</NavItem>
      {showSetup && <><div className="side-rule" /><NavItem icon="setup" to={{ name: "setup", code }} active={route.name === "setup"}>Project setup</NavItem></>}
    </nav>} />
  );
}

function ProjectNavMini({ code, route, admin }: { code: string; route: Route; admin: ReactNode }) {
  const ctx = useRpc("project_context", { code });
  const showSetup = !!ctx.data?.can_setup;
  const mods = ctx.data ? sidebarModules(ctx.data.modules) : [];
  return (
    <SideParts admin={admin} top={<>
      <a className="brand mini" href={href({ name: "projects" })} aria-label="BuildNexx"><BrandMark size={26} label="BuildNexx" /></a>
      <NavIcon to={{ name: "projects" }} icon="home" name="All projects" />
      <ProjectSwitcher code={code} name={ctx.data?.project.name ?? ""} mini />
    </>} menu={<nav className="nav-mini">
      <NavIcon to={{ name: "dashboard", code }} icon="dashboard" name="Dashboard" active={route.name === "dashboard"} />
      {mods.map((m) => (
        <NavIcon key={m.id} icon={MODULE_ICON[m.id] ?? "issues"} name={m.name} soon={m.state === "soon"} to={moduleEntryRoute(m.id, code)}
          active={m.id === "issues" && ["issues", "component", "issue"].includes(route.name)} />
      ))}
      <NavIcon to={{ name: "activity", code }} icon="activity" name="Activity" active={route.name === "activity"} />
      {showSetup && <><div className="side-rule mini" /><NavIcon icon="setup" name="Project setup" to={{ name: "setup", code }} active={route.name === "setup"} /></>}
    </nav>} />
  );
}

/** Project switcher: dropdown with search by name / code / client (A2.1). Collapsed: a short code chip ("024"). */
function ProjectSwitcher({ code, name, mini }: { code: string; name: string; mini?: boolean }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const list = useRpc("list_projects");
  const box = useRef<HTMLDivElement>(null);
  const menuBox = useRef<HTMLDivElement>(null);
  useOutsideAll([box, menuBox], () => setOpen(false), open);
  useEsc("popup", () => { if (!open) return false; setOpen(false); return true; }, open);
  // Round 16: ★ starred projects first (then the rest in their usual order).
  const hits = useMemo(() => (list.data ?? []).filter((p) =>
    `${p.name} ${p.code} ${p.client}`.toLowerCase().includes(q.trim().toLowerCase()))
    .map((p, i) => ({ p, i })).sort((a, b) => Number(b.p.starred) - Number(a.p.starred) || a.i - b.i).map((x) => x.p), [list.data, q]);
  return (
    <div className={`switcher${mini ? " mini" : ""}`} ref={box}>
      {mini
        ? <button className="switcher-chip" onClick={() => setOpen(!open)} aria-expanded={open} aria-label="Switch project" title={`Switch project · ${code}`}>{code.replace(/^BIM-/, "")}</button>
        : <button className="switcher-btn" onClick={() => setOpen(!open)} aria-expanded={open} aria-label="Switch project">
            <span className="row"><CodeChip code={code} /><span className="caret"><Icon name="arrow_drop_down" /></span></span>
            <span className="name">{name}</span>
          </button>}
      {open && (
        <SideMenu anchor={box} open={open} side={mini ? "right" : "below"} className="switcher-menu" menuRef={menuBox}
          onKeyDown={(e) => { menuKeys(e); if (e.key === "Escape") { e.preventDefault(); setOpen(false); } }}>
          <div className="row gap6">
            <SearchBox placeholder="Search name, code or client" value={q} onChange={setQ} autoFocus
              onKeyDown={(e) => { if (e.key === "Enter" && hits[0]) { setOpen(false); setQ(""); navigate({ name: "dashboard", code: hits[0].code }); } }} />
            <InfoIcon tip={["Search by project name, code or client.", "Only your projects are listed.", "★ Starred projects are listed first."]} />
          </div>
          {hits.map((p) => (
            <button key={p.id} className={`menu-item${p.code === code ? " current" : ""}`}
              onClick={() => { setOpen(false); setQ(""); navigate({ name: "dashboard", code: p.code }); }}>
              <CodeChip code={p.code} /><span className="ellipsis grow">{p.name}</span>{p.starred && <span className="star-btn on" aria-label="Starred"><Icon name="star" /></span>}
            </button>
          ))}
          {hits.length === 0 && <p className="muted small pad">No projects match</p>}
        </SideMenu>
      )}
    </div>
  );
}

function AdminLink({ route, mini }: { route: Route; mini?: boolean }) {
  const { me } = useSession();
  if (!me.is_admin) return null;                             // Admin console: super admin and Administrators (0010)
  if (mini) return <><div className="side-rule mini" /><NavIcon to={{ name: "admin" }} icon="admin" name="Admin console" active={route.name === "admin"} /></>;
  return <><div className="side-rule" /><p className="section">Admin</p><NavItem icon="admin" to={{ name: "admin" }} active={route.name === "admin"}>Admin console</NavItem></>;
}

/** A1.1 profile menu — closes on outside click or Esc (code list #1). Collapsed: the K circle, menu opens beside it.
 *  v0.9.0 (round 31 #5): no bell — the count badge sits on K; the menu's "Notifications" opens A8. */
function ProfileMenu({ mini }: { mini?: boolean }) {
  const { me, signOut } = useSession();
  const [open, setOpen] = useState(false);
  const badge = useBadge();                                  // round 31 #5: the count sits on K and on Notifications
  const box = useRef<HTMLDivElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  useOutsideAll([box, menu], () => setOpen(false), open);
  useEsc("popup", () => { if (!open) return false; setOpen(false); return true; }, open);
  useEffect(() => { if (open) focusMenu(menu.current); }, [open]);
  return (
    <div className={`profile${mini ? " mini" : ""}`} ref={box}>
      {open && (
        <SideMenu anchor={box} open={open} side={mini ? "right" : "above"} className="profile-menu" menuRef={menu} onKeyDown={menuKeys}>
          <p className="muted small">{me.email}</p>
          <a className="menu-item" href={href({ name: "notifications" })} onClick={() => setOpen(false)} data-testid="menu-notifications">
            <Icon name="notifications" /> Notifications{badge > 0 && <span className="menu-badge" data-testid="menu-badge">{badge}</span>}</a>
          <a className="menu-item" href={href({ name: "settings" })} onClick={() => setOpen(false)}><Icon name="settings" /> Settings</a>
          <button className="menu-item" onClick={signOut}><Icon name="logout" /> Sign out</button>
        </SideMenu>
      )}
      <button className={`profile-btn${open ? " on" : ""}`} onClick={() => setOpen(!open)} aria-label="Profile menu" aria-expanded={open} title={mini ? me.full_name : undefined}>
        <span className="avatar">{me.full_name[0]}{badge > 0 && <span className="avatar-badge" data-testid="avatar-badge" aria-label={`${badge} notifications`}>{badge}</span>}</span>
        {!mini && <><span className="who"><b>{me.full_name}</b><small>{me.is_super_admin ? "Super admin" : me.company_role_name ?? "Member"}</small></span>
          <span className="caret"><Icon name="expand_less" /></span></>}
      </button>
    </div>
  );
}
